import Container from 'typedi';
import { LoggerProvider } from '../logger';
import { IPostMessageHandler } from '../post-message';
import { MembershipActions } from '../../models/enums/general-enums';
import { NativeUCAppType } from '../player/enums/consts';
import { PlayerService } from '../player/player.service';
import PostLoginApi from '../../../APIs/PostLoginApi';
import { RegistrationAuthenticationService } from '../authentication/services/registration-authentication.service';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { Utils } from '../utils';
import { WebLoginService } from '../web-login/web.login.service';
import { SessionSimpleStoreService } from '../storage/implementations/simple-store';
import { ILoginOptions } from '../authentication/models';
import { IActionChannel } from '../post-login/models/IPostLoginActionsState';
import { NativeUtils } from '../native/native.utils';
import { Navigation } from '../navigation';
import { AppState } from '../app-state';
import { IElasticEventData } from '../tracking/models/interfaces/IElasticEventData';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@unified-client/event-formatter';
import { ClickStreamTrackingProvider } from '../tracking';
import { TrackingElasticEvent } from '../tracking/enums/consts';
import { ILogger } from '@sparkware/uc-sdk-core';
import { ViewInjector } from '../view-injector';
import { UrlUtils } from '../utils/urlUtils';

export class RegistrationHandler implements IPostMessageHandler {
    private _logger: ILogger;
    private _sessionSimpleStoreService: SessionSimpleStoreService;
    private _webLoginService: WebLoginService;
    private _utils: Utils;
    private _playerService: PlayerService;
    private _registrationAuthenticationService: RegistrationAuthenticationService;
    private _viewInjector: ViewInjector;
    protected readonly _navigation: Navigation;
    private readonly _appState: AppState;
    private readonly _eventFormatterBuilderFactory: EventFormatterBuilderFactory;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _urlUtils: UrlUtils;

    public get name(): string {
        return 'RegistrationPostMessageHandler';
    }

    constructor() {
        this._viewInjector = Container.get(ViewInjector);
        this._logger = Container.get(LoggerProvider).getLogger(this.name);
        this._playerService = Container.get(PlayerService);
        this._registrationAuthenticationService = Container.get(RegistrationAuthenticationService);
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._utils = Container.get(Utils);
        this._webLoginService = Container.get(WebLoginService);
        this._navigation = Container.get(Navigation);
        this._appState = Container.get(AppState);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            this._eventFormatterBuilderFactory.createEventFormatterBuilder('RegistrationHandler');
        this._urlUtils = Container.get(UrlUtils);
    }

    public handler = async (event: any): Promise<boolean> => {
        return this._handleMessages(event);
    };

    private _handleMessages = async (event: MessageEvent): Promise<boolean> => {
        this._logger.debug(`Handler RegistrationPostMessageHandler is going to be executed...`);

        const formatter = this._eventFormatterBuilder.createFormatter(
            'registrationHandlerHandleMessages',
        );
        const eventData: IElasticEventData = {
            event: TrackingElasticEvent.WindowMessage,
            dataString: JSON.stringify(event.data),
        };
        this._clickStreamTrackingProvider.sendElasticEvent(
            formatter,
            'Handler RegistrationPostMessageHandler is going to be executed',
            eventData,
        );

        if (!event.data) return false;

        const message = this._utils.TryParseJSON(event.data);

        if (!message) return false;
        if (!message.ActionId) return false;

        switch (message.ActionId) {
            case MembershipActions.Autologin: {
                this._checkLowFunds().then(() => {
                    this._autoLogin(message);
                });

                break;
            }
            case MembershipActions.Deposit: {
                if (message.UserInfo) {
                    const channel: IActionChannel = {
                        Source: 'Registration',
                        Area: 'Straight to Cashier',
                        Element: 'UD',
                    };

                    this._sessionSimpleStoreService.set(
                        StorageItemEnum.CashierOpenStartTimestamp,
                        String(Date.now()),
                    );

                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            OpenCashier: {
                                channel,
                                enabled: true,
                            },
                        },
                    };
                    this._autoLogin(message, loginOptions);
                }

                break;
            }
            case MembershipActions.UploadDocument: {
                if (message.UserInfo) {
                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            OpenCashierUploadDocument: {
                                enabled: true,
                            },
                        },
                    };

                    this._autoLogin(message, loginOptions);
                }
                break;
            }
            case MembershipActions.ShowWebLogin: {
                await this._appState.setBottomBarVisibility();
                this._viewInjector.CloseWrapDiv();
                this._webLoginService.Show();

                break;
            }
            case MembershipActions.Back:
            case MembershipActions.CloseWindow: {
                await this._appState.setBottomBarVisibility();
                this._viewInjector.CloseWrapDiv();
                break;
            }
            case MembershipActions.BankId: {
                if (message.AppUrl) {
                    this._urlUtils.openMobileAppWithFallback(
                        message.AppUrl,
                        'https://play.google.com/store/apps/details?id=com.bankid.bus',
                        'https://apps.apple.com/se/app/bankid-s%C3%A4kerhetsapp/id433151512?l=en',
                    );
                }
                break;
            }
            case MembershipActions.StoreRegistrationCookiesData: {
                await NativeUtils.showBottomBar(false);

                if (message.StoreRegistrationCookiesData) {
                    localStorage.setItem(
                        'registration_data',
                        JSON.stringify(message.StoreRegistrationCookiesData),
                    );
                }
                break;
            }
            case MembershipActions.DownloadNativeSportApp: {
                this._playerService.OpenNativeApp({ Type: NativeUCAppType.Sport888 });
                break;
            }
            case MembershipActions.ShowKYC: {
                if (message.UserInfo) {
                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            ShowKYC: {
                                enabled: true,
                            },
                        },
                    };

                    this._autoLogin(message, loginOptions);
                }
                break;
            }
            case MembershipActions.RedirectClient: {
                if (message.Url)
                    this._navigation.redirectOrNativeOpenExternalUrl({ url: message.Url });
                break;
            }
        }

        return true;
    };

    private _checkLowFunds = async () => {
        const { response, errorResponse } = await PostLoginApi.LowFundsAlreadyDisplayed();

        return !!response;
    };

    private _autoLogin = async (message: MessageEvent, options?: ILoginOptions) => {
        this._webLoginService.Hide();
        await this._registrationAuthenticationService.autoLogin(message, options);
        this._viewInjector.CloseWrapDiv();
    };
}
